import { useEffect, useState } from 'react';
import { api, clearBackupJobs } from '../../lib/api';
import {
  Database, Download, RotateCcw, Plus, ChevronLeft, ChevronRight,
  AlertTriangle, CheckCircle, Clock, Loader2, XCircle, Upload, Trash2
} from 'lucide-react';
import { apiFetch } from '../../lib/api';

interface BackupJob {
  id: string;
  tenant_id: string;
  type: string;
  status: string;
  file_path: string | null;
  size_bytes: number;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
}

interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  status: string;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL');
}

const PAGE_SIZE = 10;

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
    case 'running': return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
    case 'pending': return <Clock className="w-4 h-4 text-amber-500" />;
    default: return <Database className="w-4 h-4 text-gray-400" />;
  }
};

const statusLabels: Record<string, string> = {
  completed: 'Voltooid',
  failed: 'Mislukt',
  running: 'Bezig',
  pending: 'Wachtend',
  deleted: 'Verwijderd',
};

const statusColors: Record<string, string> = {
  completed: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  deleted: 'bg-gray-50 text-gray-500',
  pending: 'bg-amber-50 text-amber-700',
  running: 'bg-blue-50 text-blue-700',
};

export default function Backups() {
  const [backups, setBackups] = useState<BackupJob[]>([]);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<{ tenantId: string; backupId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [page, setPage] = useState(1);
  const [filterTenant, setFilterTenant] = useState('');
  const [importTenantId, setImportTenantId] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);

  const [importLoading, setImportLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    setError(null);
    try {
      const [backupsRes, tenantsRes] = await Promise.all([
        api.get<{ backups: BackupJob[] }>('/backups'),
        api.get<TenantInfo[]>('/tenants'),
      ]);
      setBackups((backupsRes as any).backups ?? []);
      setTenants(((tenantsRes as any).tenants ?? tenantsRes) as TenantInfo[]);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function getTenantName(tenantId: string): string {
    return tenants.find(t => t.id === tenantId)?.name ?? tenantId;
  }

  async function handleCreateBackup() {
    if (!selectedTenantId) return;
    setActionLoading('create');
    setError(null);
    setSuccess(null);
    try {
      await api.post(`/backups/${selectedTenantId}`);
      setSuccess(`Backup gestart voor ${getTenantName(selectedTenantId)}`);
      await fetchData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRestore(tenantId: string, backupId: string) {
    setConfirmRestore(null);
    setActionLoading(`restore-${backupId}`);
    setError(null);
    setSuccess(null);
    try {
      await api.post(`/backups/${tenantId}/restore/${backupId}`);
      setSuccess('Herstel succesvol voltooid');
      await fetchData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleImport() {
    if (!importTenantId || !importFile) return;
    setImportLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const formData = new FormData();
      formData.append('backup', importFile);
      await apiFetch(`/backups/${importTenantId}/import`, {
        method: 'POST',
        body: formData,
        headers: {}, // let browser set content-type with boundary
      });
      setSuccess(`Backup geimporteerd naar ${getTenantName(importTenantId)}`);
      setImportFile(null);
      await fetchData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setImportLoading(false);
    }
  }

  // Filter and paginate
  const filteredBackups = filterTenant
    ? backups.filter(b => b.tenant_id === filterTenant)
    : backups;
  const sortedBackups = [...filteredBackups].sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  );
  const totalPages = Math.max(1, Math.ceil(sortedBackups.length / PAGE_SIZE));
  const paginatedBackups = sortedBackups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const activeTenants = tenants.filter(t => t.status === 'active');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backups</h1>
          <p className="text-sm text-gray-500 mt-1">Beheer backups van alle tenants</p>
        </div>
        <div className="flex items-center gap-2">
          {backups.length > 0 && !confirmClear && (
            <button onClick={() => setConfirmClear(true)} className="btn-secondary text-sm text-red-600 hover:bg-red-50 flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Alles wissen
            </button>
          )}
          {confirmClear && (
            <>
              <span className="text-sm text-red-600">Alle backups en bestanden verwijderen?</span>
              <button
                onClick={async () => { setClearing(true); setError(null); try { const r = await clearBackupJobs(); setBackups([]); setSuccess(`${r.deleted} backup(s) verwijderd.`); } catch (e: any) { setError(e.message); } finally { setClearing(false); setConfirmClear(false); } }}
                disabled={clearing}
                className="btn-secondary text-sm text-red-700 bg-red-50 hover:bg-red-100"
              >
                {clearing ? 'Wissen...' : 'Ja, wissen'}
              </button>
              <button onClick={() => setConfirmClear(false)} className="btn-secondary text-sm">Annuleren</button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Create backup */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Handmatige backup</h2>
        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-xs">
            <label className="block text-xs text-gray-500 mb-1">Website</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="input text-sm"
            >
              <option value="">Kies een website...</option>
              {activeTenants.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleCreateBackup}
            disabled={!selectedTenantId || actionLoading === 'create'}
            className="btn-primary text-sm flex items-center gap-2"
          >
            {actionLoading === 'create' ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Bezig...</>
            ) : (
              <><Plus className="w-4 h-4" /> Backup starten</>
            )}
          </button>
        </div>
      </div>

      {/* Import backup */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Backup importeren</h2>
        <p className="text-xs text-gray-500 mb-3">Importeer een backup-archief (.tar.gz) in een bestaande tenant. Alle huidige data wordt overschreven.</p>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[160px] max-w-xs">
            <label className="block text-xs text-gray-500 mb-1">Doel-website</label>
            <select
              value={importTenantId}
              onChange={(e) => setImportTenantId(e.target.value)}
              className="input text-sm"
            >
              <option value="">Kies een website...</option>
              {activeTenants.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px] max-w-sm">
            <label className="block text-xs text-gray-500 mb-1">Backup bestand</label>
            <input
              type="file"
              accept=".tar.gz,.tgz"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="input text-sm"
            />
          </div>
          <button
            onClick={handleImport}
            disabled={!importTenantId || !importFile || importLoading}
            className="btn-primary text-sm flex items-center gap-2"
          >
            {importLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Importeren...</>
            ) : (
              <><Upload className="w-4 h-4" /> Importeren</>
            )}
          </button>
        </div>
      </div>

      {/* Backup history */}
      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Geschiedenis</h2>
          <div className="flex items-center gap-2">
            <select
              value={filterTenant}
              onChange={(e) => { setFilterTenant(e.target.value); setPage(1); }}
              className="input text-xs py-1 w-44"
            >
              <option value="">Alle websites</option>
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
          </div>
        ) : paginatedBackups.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">
            {filterTenant ? 'Geen backups voor deze website' : 'Nog geen backups'}
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-50">
              {paginatedBackups.map(b => (
                <div key={b.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusIcon status={b.status} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {getTenantName(b.tenant_id)}
                        </span>
                        <span className={`inline-flex px-1.5 py-0.5 text-xs rounded ${statusColors[b.status] ?? 'bg-gray-50 text-gray-500'}`}>
                          {statusLabels[b.status] ?? b.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                        <span>{formatDate(b.completed_at ?? b.started_at)}</span>
                        {b.size_bytes > 0 && <span>{formatBytes(b.size_bytes)}</span>}
                        <span className="capitalize">{b.type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {b.status === 'completed' && b.file_path && (
                      <>
                        <button
                          onClick={() => window.location.href = `/api/backups/download/${b.id}?token=${localStorage.getItem('occiput_token')}`}
                          className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfirmRestore({ tenantId: b.tenant_id, backupId: b.id })}
                          className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Herstel"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {b.error_message && (
                      <span className="text-red-400 text-xs ml-2" title={b.error_message}>
                        <AlertTriangle className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                <span className="text-xs text-gray-400">
                  {sortedBackups.length} backup{sortedBackups.length !== 1 ? 's' : ''} totaal
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-500 px-2">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Restore confirmation modal */}
      {confirmRestore && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <RotateCcw className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Backup herstellen?</h3>
                <p className="text-sm text-gray-500">Dit kan niet ongedaan worden gemaakt.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Alle huidige data van <strong>{getTenantName(confirmRestore.tenantId)}</strong> wordt vervangen met deze backup.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmRestore(null)} className="btn-secondary text-sm">
                Annuleren
              </button>
              <button
                onClick={() => handleRestore(confirmRestore.tenantId, confirmRestore.backupId)}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
              >
                Ja, herstel backup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
